/**
 * Error Boundary for Owner Dashboard
 * Prevents whitescreens and provides graceful error recovery
 */

import { Component } from 'react';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Reload the page to reset state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = window.location.hostname.includes('.dev.') || 
                    window.location.hostname.includes('localhost') ||
                    new URLSearchParams(window.location.search).get('debug') === '1';

      return (
        <div style={styles.container}>
          <div style={styles.panel}>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              The dashboard encountered an error. Please try again.
            </p>
            <button onClick={this.handleRetry} style={styles.retryButton}>
              Retry
            </button>
            {isDev && this.state.error && (
              <details style={styles.debugDetails}>
                <summary style={styles.debugSummary}>Debug Information (DEV only)</summary>
                <pre style={styles.debugContent}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
  },
  panel: {
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-6)',
    maxWidth: '600px',
    width: '100%',
    boxShadow: 'var(--shadow-lg)',
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    marginBottom: 'var(--spacing-4)',
  },
  message: {
    fontSize: 'var(--typography-level-4-size)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-6)',
    lineHeight: 'var(--line-height-relaxed)',
  },
  retryButton: {
    padding: 'var(--spacing-3) var(--spacing-6)',
    backgroundColor: 'var(--color-primary-base)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--typography-level-4-size)',
    fontWeight: 'var(--font-weight-semibold)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
  },
  debugDetails: {
    marginTop: 'var(--spacing-6)',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--color-bg-tertiary)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border-light)',
  },
  debugSummary: {
    fontSize: 'var(--typography-level-5-size)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    marginBottom: 'var(--spacing-2)',
  },
  debugContent: {
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'monospace',
    color: 'var(--color-text-primary)',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '400px',
    padding: 'var(--spacing-2)',
    backgroundColor: 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-sm)',
  },
};

